import React, { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react'
import styles from '../ProductActivityPage.module.css'
import o from '../../components/Overlay.module.css'
import pv from './Overlays.module.css'
import { apiJson } from '../../lib/api'
import { useLayer } from '../../components/layers'
import { PendingButton, useSlow } from '../../components/pending'
import { fmtStamp } from '../timesheets/time'
import { RuleLineList } from './RuleLines'
import { GPS_FROM_LABEL, STATE_TEXT, clock, dayLabel, kg, pct, stampParts, type Adjustment, type BulkEdit, type Cycle, type CycleClaim, type Detail, type EvidencePoint, type Option, type QualityScan } from './model'
import { itemName, taglessTail } from './itemName'
import { PhotoRow, photoRefs, type PhotoRef } from './Photos'

// Leaflet loads only when a cycle has a point to show.
const EvidenceMap = lazy(() => import('./EvidenceMap'))

// The cycle drawer (V4 paDrawer): tiles, the four tabs and the Resolve /
// Correct menu. It explains what the engine recorded: the claims it read into
// the cycle and the role it gave each (selected, superseded, not selected,
// staged), its quality scans, and the hand edits on top. The rule lines are
// the engine's own; a cycle recorded before the preassign step has none, and
// reads from its one claim as it always did.

export type Tab = 'timeline' | 'evidence' | 'rules' | 'audit'
type Act = 'worker' | 'weight' | 'separate' | 'delete'

interface Props {
  user: { getIdToken: () => Promise<string> } | null
  cycleId: string
  workers: Option[]
  onClose: () => void
  onWritten: () => void
  toast: (msg: string) => void
  // Opens the cycle's batch in Batches (§7k.5 cross-linking).
  onBatch?: (c: Cycle) => void
  // The tab it opens on; the Strike Board links to a cycle's evidence.
  initialTab?: Tab
  // Opens another cycle in the drawer: a separate pick's audit entry links
  // the cycle it made and the one it came from (V4 paAdjItems onLink).
  onOpenCycle?: (id: string) => void
}

const TL = {
  selected: { label: 'Selected', bg: '#f1f4e8', fg: '#3f4a2c', dot: '#7a8a5e' },
  attached: { label: 'Attached', bg: '#fdf1e8', fg: '#8c491a', dot: '#c67139' },
  provisional: { label: 'Provisional', bg: '#fdf1e8', fg: '#8c491a', dot: '#e8b98c' },
  unmatched: { label: 'Unapplied', bg: '#f8ddcd', fg: '#6e3312', dot: '#a2512a' },
  excluded: { label: 'Excluded', bg: '#f6f0e6', fg: '#857c6d', dot: '#a89e8c' },
  superseded: { label: 'Superseded', bg: '#f6f0e6', fg: '#857c6d', dot: '#a89e8c' },
  lost: { label: 'Not selected', bg: '#f6f0e6', fg: '#857c6d', dot: '#a89e8c' },
  staged: { label: 'Staged', bg: '#f8ddcd', fg: '#6e3312', dot: '#a2512a' },
  awaited: { label: 'Missing', bg: '#f8ddcd', fg: '#6e3312', dot: '#a2512a' },
}
type TlState = keyof typeof TL

interface TlItem {
  key: string
  at: number
  t: string
  kind: string
  who: string
  state: TlState
  detail: string
  rule: string
  // The point the device recorded on the event, and whether it is the winning
  // claim or its receipt (the Evidence map's dashed line).
  gps?: { lat: number; lng: number } | null
  link?: 'claim' | 'receipt'
  // The photos the phone sent for this event.
  photos?: PhotoRef[]
}

// V4 paEvTone: a point's colour by the state the engine gave its event.
const EV_TONE: Partial<Record<TlState, string>> = {
  selected: '#c67139',
  attached: '#7a8a5e',
  provisional: '#c78f2c',
  lost: '#a89e8c',
  superseded: '#a89e8c',
  excluded: '#a89e8c',
  unmatched: '#a2512a',
  staged: '#a2512a',
}

// h 2709 (V4 paEvPoints): every event a device recorded a point on, newest
// first like the timeline. Hollow points are ones the engine did not select.
function evidencePoints(tl: TlItem[]): EvidencePoint[] {
  return tl
    .filter((e): e is TlItem & { gps: { lat: number; lng: number } } => !!e.gps)
    .map((e) => ({
      key: e.key,
      lat: e.gps.lat,
      lng: e.gps.lng,
      t: e.t,
      kind: e.kind,
      who: e.who || '—',
      state: TL[e.state].label,
      tone: EV_TONE[e.state] || '#857c6d',
      off: e.state !== 'selected' && e.state !== 'attached' && e.state !== 'provisional',
      link: e.link,
    }))
}

// A scan is placed by when it was taken where the device said so (28g), and
// read in the site clock the server gives.
const scanTime = (q: QualityScan) => q.captured_at || q.server_received_at || q.created_at
const scanClock = (q: QualityScan) => q.local_time || clock(scanTime(q))
// Tied by the engine, but its deduction waits for the day to close (h 2512).
const heldScan = (q: QualityScan) => q.matched && !!q.provisional && !q.attached
// D7.3/D7.6: kept and applied nothing, waiting for a person to accept it in Late data.
const reviewHeld = (q: QualityScan) => q.outcome === 'held_for_review' && !q.matched && !q.attached && !q.review_accepted_at
const SET_SOURCE_LABEL = { device: 'device', team_derived: 'team-derived', control_override: 'Control override' } as const
const scanName = (q: QualityScan) => (q.quality_name || 'Quality scan') + (q.quality_archived ? ' (archived)' : '')
const lowFirst = (t: string) => (t ? t.charAt(0).toLowerCase() + t.slice(1) : '')
const liveEdits = (adj: Adjustment[]) => adj.filter((a) => a.kind === 'change' && !a.undone)
const unappliedScans = (d: Detail) => d.quality_scans.filter((q) => !q.matched && !q.attached)
// The site-local time where the server gave one for this instant.
const whenOf = (c: Cycle, iso: string) => (iso === c.timing?.history_id && c.eff_time ? c.eff_time : clock(iso))
// h 4476-4480: a receipt is the scanner's (the named operator, else the
// device), never the picker whose claim it credits.
const receipterOf = (x: { receipter?: string | null; producer_id: string }) => x.receipter || x.producer_id
// Selfscan Harvest: the picker scanned the item in Crew themselves, so the
// cycle has a claim and photos but no receipt by anyone else.
const SELF_SCAN_RULE = 'Self-scanned: the picker put their name on it in Crew'
const selfScanned = (d: Detail) =>
  d.claims?.length ? d.claims.some((x) => x.role === 'pick_selected' && !!x.self_scan) : !!d.raw_claim?.self_scan
// h 4540: the receipt a claimed cycle is still waiting for.
const awaitingReceipt = (c: Cycle) => !!c.timing && !c.timing.pick_history_id
const lostClaims = (d: Detail) => (d.claims || []).filter((x) => x.role === 'preassign_lost')
const ACT_TITLE: Record<Act, string> = {
  weight: 'Correct gross weight',
  worker: 'Credit this cycle to',
  separate: 'Recognise which claim as a separate pick?',
  delete: 'Delete this cycle?',
}

const CLAIM_STATE: Record<CycleClaim['role'], TlState> = {
  pick_selected: 'selected',
  pick_superseded: 'superseded',
  preassign_won: 'selected',
  preassign_lost: 'lost',
  staged: 'staged',
  // V4: a claim recognised as a separate pick reads Excluded here; it now
  // credits the cycle it made.
  separated: 'excluded',
}

// Add weight to bin: the receiver typed the item's weight in Crew after receipting it.
const CREW_WEIGH_RULE = 'Weight added in Crew by the receiver, as a scale would'

function claimRule(c: CycleClaim, credited: boolean, weighedLater = false): string {
  if (c.role === 'separated') return 'Manual resolution: now its own cycle'
  if (c.role === 'staged') return 'Late data = Stage for approval'
  if (c.role === 'pick_superseded') return weighedLater && !c.crew_weigh ? 'Weighed later in Crew: that weight replaced this one' : 'Repeated PICK inside the reuse period'
  if (c.crew_weigh && c.role === 'pick_selected') return CREW_WEIGH_RULE
  if (c.self_scan && c.role === 'pick_selected') return SELF_SCAN_RULE
  if (c.role === 'pick_selected') return credited ? 'Claim accepted: this receipt credited the pick' : 'Receipted with no eligible claim: waiting for a worker'
  if (c.rule === 'pick_worker') return 'The PICK names its own worker'
  return 'Competing PREASSIGN = First claim wins'
}

interface BatchTlItem {
  key: string
  stamp: string | null
  kind: string
  who: string
  state: string
  detail: string
  on: boolean
}

const temp = (n: number | null) => (n === null ? '' : ' · ' + n.toFixed(1) + '°C')

// h 4544-4567 (V4 paBatchTl): the bin's lifecycle, newest first. Only steps
// that happened: packhouse receipting and bin weighing are optional for many
// clients, so their absence is silence. An open bin says so, because that is
// its current state rather than a skipped step.
function batchTimeline(d: Detail): BatchTlItem[] {
  const b = d.batch
  if (!b) return []
  const bin = b.bin_code ? 'Bin ' + b.bin_code : 'Batch ' + b.batch_code
  const rows: BatchTlItem[] = []
  if (b.receipted_local)
    rows.push({ key: 'b-receipted', stamp: b.receipted_local, kind: 'BATCH RECEIPTED', who: b.receipted_by || '', state: 'Receipted', detail: bin + ' booked in at the packhouse' + temp(b.packhouse_temp_c), on: true })
  if (b.weighed_kg !== null && b.weighed_kg > 0)
    rows.push({
      key: 'b-weighed',
      stamp: b.weighed_local,
      kind: 'BATCH WEIGHED',
      who: b.weighed_by || '',
      state: 'Weighed',
      detail: kg(b.weighed_kg) + (b.weighed_source ? ' · ' + b.weighed_source : '') + ': shared pro-rata across the bin’s cycles',
      on: true,
    })
  if (b.completed_local)
    rows.push({ key: 'b-completed', stamp: b.completed_local, kind: 'BATCH COMPLETED', who: b.completed_by || '', state: 'Completed', detail: bin + ' closed in the field' + temp(b.pick_temp_c), on: true })
  // A bin receipted with no field completion is an exception in Batches, not
  // an open bin: it is no longer taking cycles.
  else if (!b.receipted_local)
    rows.push({ key: 'b-open', stamp: null, kind: 'BATCH OPEN', who: '', state: 'Open', detail: bin + ' is still taking cycles', on: false })
  // Site-clock stamps sort as text; the open row has none and heads the list.
  return rows.sort((x, y) => (y.stamp || '9').localeCompare(x.stamp || '9'))
}

function timeline(d: Detail): TlItem[] {
  const c = d.cycle
  const out: TlItem[] = []
  if (d.claims?.length) {
    const weighedLater = d.claims.some((x) => x.role === 'pick_selected' && !!x.crew_weigh)
    for (const x of d.claims) {
      const pick = x.claim_type !== 'PREASSIGN' && !x.self_scan
      const who = x.crew_weigh ? 'Dataphyll Crew' : pick ? receipterOf(x) : x.worker_name || 'Unknown worker'
      // The weight, whom the selected receipt credited, and the device when
      // the row is not already named after it.
      const credited = x.role === 'pick_selected' && c.captured.worker_name ? 'credited to ' + c.captured.worker_name : ''
      out.push({
        key: x.id,
        at: new Date(x.captured_at).getTime(),
        t: whenOf(c, x.captured_at),
        kind: x.crew_weigh ? 'WEIGHED' : x.self_scan ? 'SELF-SCAN' : pick ? 'RECEIPTED' : 'PREASSIGN',
        who,
        state: x.role === 'pick_selected' && c.status === 'excluded' ? 'excluded' : CLAIM_STATE[x.role],
        detail: x.crew_weigh
          ? [x.gross_kg === null ? 'Not weighed' : kg(x.gross_kg) + ' gross', 'weighed in Crew', credited].filter(Boolean).join(' · ')
          : pick
          ? [x.gross_kg === null ? 'Not weighed' : kg(x.gross_kg) + ' gross', credited, who === x.producer_id ? '' : x.producer_id].filter(Boolean).join(' · ')
          : x.role === 'separated'
            ? 'Recognised as a separate pick'
            : x.self_scan
              ? [x.gross_kg === null ? 'Not weighed' : kg(x.gross_kg) + ' gross', 'scanned in Crew'].join(' · ')
              : x.producer_id,
        rule: claimRule(x, !!c.captured.human_id, weighedLater),
        gps: x.gps,
        link: x.role === 'preassign_won' ? 'claim' : x.role === 'pick_selected' ? 'receipt' : undefined,
        photos: photoRefs(x.device_event_id, x.photos),
      })
    }
  } else if (d.raw_claim) {
    const self = !!d.raw_claim.self_scan
    const who = self ? c.captured.worker_name || 'Unknown worker' : receipterOf(d.raw_claim)
    out.push({
      key: 'claim',
      at: new Date(d.raw_claim.device_captured_at).getTime(),
      t: c.eff_time || clock(d.raw_claim.device_captured_at),
      // The receipt is the moment the claim became a credited pick (V4); a
      // self-scan is the picker's own claim.
      kind: self ? 'SELF-SCAN' : 'RECEIPTED',
      who,
      state: c.status === 'excluded' ? 'excluded' : 'selected',
      detail: [
        c.captured.gross_kg === null ? 'Not weighed' : kg(c.captured.gross_kg) + ' gross',
        self ? 'scanned in Crew' : c.captured.worker_name ? 'credited to ' + c.captured.worker_name : '',
        self || who === d.raw_claim.producer_id ? '' : d.raw_claim.producer_id,
      ].filter(Boolean).join(' · '),
      rule: self
        ? SELF_SCAN_RULE
        : c.captured.human_id
          ? 'Claim accepted: this receipt credited the pick'
          : 'Receipted with no eligible claim: waiting for a worker',
      gps: d.raw_claim.gps,
      link: 'receipt',
      photos: photoRefs(d.raw_claim.device_event_id, d.raw_claim.photos),
    })
  }
  // h 4540: an awaited receipt still reads RECEIPT, in its missing state.
  if (awaitingReceipt(c)) {
    out.push({
      key: 'awaited-receipt',
      // Nothing has happened yet, so it heads the list.
      at: Infinity,
      t: '—',
      kind: 'RECEIPT',
      who: 'Not receipted',
      state: 'awaited',
      detail: 'No receipt has been scanned for this use yet, so nothing is weighed',
      rule: 'A claim with no PICK is held as incomplete',
    })
  }
  for (const q of d.quality_scans) {
    const late = !q.attached && q.outcome === 'late'
    const inReview = reviewHeld(q)
    const state: TlState = q.attached ? 'attached' : heldScan(q) ? 'provisional' : q.matched ? 'attached' : late ? 'staged' : 'unmatched'
    const factor = q.qc_factor === null ? 'No factor recorded' : pct(q.qc_factor) + ' factor'
    out.push({
      key: q.id,
      at: new Date(scanTime(q)).getTime(),
      t: scanClock(q),
      kind: 'QUALITY',
      who: scanName(q),
      state,
      detail:
        (inReview
          ? 'Held for review · deduction not applied, accept it in Late data'
          : state === 'provisional'
          ? factor + ' · held until the day closes'
          : state === 'staged'
            ? 'Arrived after the day closed · deduction not applied'
            : state === 'unmatched'
              ? q.candidate_only ? 'One of several cycles it could belong to · deduction not applied' : 'Deduction not applied'
              : factor + (q.effective_local_time ? ' · applied at day close ' + q.effective_local_time : '')) +
        // D7.4 C: a required photo the phone has not sent (yet) reads as missing. The scan itself is read as above.
        (q.photo_required_at_capture && !q.photos?.length ? ' · photo missing' : '') +
        // No point recorded on the scan: it is placed where the item was picked or receipted.
        (q.gps && q.gps_from ? ` · no GPS on the scan, shown at ${GPS_FROM_LABEL[q.gps_from]}` : '') +
        // D7 Part 13: where the quality set came from, read-only. Absent on scans from before sets existed.
        (q.set_source ? ' · set: ' + SET_SOURCE_LABEL[q.set_source] : ''),
      // The rule wording stored with the scan when it was decided (28g).
      rule: q.attached ? 'Attached by hand' : q.rule_text || (q.matched ? 'QC association = either side of receipt' : 'Ambiguous QC stays unapplied'),
      gps: q.gps,
      photos: photoRefs(q.device_event_id, q.photos),
    })
  }
  // Newest at the top: a history reads downwards from now.
  return out.sort((a, b) => b.at - a.at)
}

// V4 paStory: template sentences over this cycle's own facts. Every clause is
// checkable against the timeline underneath it.
// h 4488-4517: who claimed it and when, who receipted it and when, which
// competing claims lost and why, then quality, exclusion and hand edits. The
// credit it names is the engine's (captured); a hand edit is narrated after.
function story(d: Detail): string[] {
  const c = d.cycle
  const out: string[] = []
  const claims = d.claims || []
  const won = claims.find((x) => x.role === 'preassign_won')
  const pick = claims.find((x) => x.role === 'pick_selected')
  const credited = c.captured.worker_name
  const item = c.tagless ? 'tagless bucket ' + taglessTail(c.item_code) : 'item ' + itemName(c)
  if (awaitingReceipt(c)) {
    const t = won ? whenOf(c, won.captured_at) : c.eff_time || '—'
    const by = won?.worker_name || c.worker_name
    out.push((c.tagless ? 'Tagless bucket ' + taglessTail(c.item_code) : 'Item ' + itemName(c)) + ' was claimed' + (by ? ' by ' + by : '') + ' at ' + t + ', but no PICK has been found for that use, so the cycle is held as incomplete.')
  } else if (pick || d.raw_claim) {
    // The receipt: the selected PICK, or a cycle's one claim from before the
    // preassign step.
    const r = pick
      ? { who: receipterOf(pick), t: whenOf(c, pick.captured_at) }
      : { who: receipterOf(d.raw_claim!), t: c.eff_time || clock(d.raw_claim!.device_captured_at) }
    if (won && credited) {
      out.push((won.worker_name || credited) + ' claimed ' + item + ' at ' + whenOf(c, won.captured_at) + '.')
      out.push(r.who + ' receipted it at ' + r.t + ', and that receipt credited the pick to ' + credited + '.')
      // §9.1: dated from the claim rather than the receipt.
      if (pick && c.timing?.history_id === c.timing?.pre_history_id && c.timing?.pre_history_id !== c.timing?.pick_history_id)
        out.push('The cycle is dated from the claim at ' + whenOf(c, won.captured_at) + ', not the receipt.')
    } else if (credited) {
      out.push(r.who + ' receipted ' + item + ' at ' + r.t + (pick ? ', and the receipt names its own worker, so it is credited to ' : ' and it is credited to ') + credited + '.')
    } else {
      out.push(r.who + ' receipted ' + item + ' at ' + r.t + ' with no eligible claim behind it, so nobody is credited yet.')
    }
  }
  // Each counter-claim by name, and the rule that set it aside.
  const lost = lostClaims(d)
  lost.slice(0, 3).forEach((x) => {
    const by = x.worker_name ? 'by ' + x.worker_name + ' ' : ''
    out.push(
      x.rule === 'pick_worker'
        ? 'A claim ' + by + 'at ' + whenOf(c, x.captured_at) + ' was not applied: the receipt names its own worker.'
        : 'A competing claim ' + by + 'at ' + whenOf(c, x.captured_at) + ' was not selected: the first claim wins.'
    )
  })
  if (lost.length > 3) out.push(lost.length - 3 + ' more competing claims were not selected; the timeline lists every one.')
  // h 2714-2725: a claim someone recognised as a use of its own.
  claims.filter((x) => x.role === 'separated').forEach((x) => {
    out.push('A competing claim ' + (x.worker_name ? 'by ' + x.worker_name + ' ' : '') + 'at ' + whenOf(c, x.captured_at) + ' was recognised by hand as a separate pick, and is now its own cycle.')
  })
  const superseded = claims.filter((x) => x.role === 'pick_superseded')
  // §9.1: a second weigh inside the reuse period supersedes the first; the
  // engine keeps the latest, so the superseded ones are always earlier.
  if (superseded.length && pick)
    out.push(
      superseded.length === 1
        ? 'The weigh at ' + whenOf(c, superseded[0].captured_at) + ' was superseded by the one at ' + whenOf(c, pick.captured_at) + ', inside the reuse period.'
        : superseded.length + ' earlier weighs were superseded by the one at ' + whenOf(c, pick.captured_at) + ', inside the reuse period.'
    )
  const staged = claims.filter((x) => x.role === 'staged').length
  if (staged) out.push(staged === 1
    ? 'One claim arrived after the day closed, so it is staged for approval and not applied.'
    : staged + ' claims arrived after the day closed, so they are staged for approval and not applied.')

  const attached = d.quality_scans.find((q) => (q.matched || q.attached) && !heldScan(q))
  const held = d.quality_scans.find(heldScan)
  const unapplied = unappliedScans(d)[0]
  const inReview = d.quality_scans.find(reviewHeld)
  if (c.gross_kg !== null) {
    if (c.qc_factor !== null && attached)
      out.push(
        'The quality scan at ' + scanClock(attached) + ' recorded ' + (c.quality_name || 'a fault') + ', so ' + kg(c.gross_kg) +
          ' gross was credited at ' + pct(c.qc_factor) + ': ' + kg(c.credited_kg) + '. Gross weight is never overwritten.' +
          (attached.effective_at
            ? ' The deduction was applied when the day closed' + (attached.effective_local_time ? ' at ' + attached.effective_local_time : '') + '.'
            : attached.attached
              ? ' It was attached by hand, so it applied at once.'
              : // §9.1: a deduction is provisional until the day closes.
                d.day_state === 'LIVE' ? ' The deduction is provisional until the day closes.' : '')
      )
    else if (held)
      // h 2512: tied, but credited weight does not move until the close.
      out.push(
        'The quality scan at ' + scanClock(held) + ' recorded ' + (held.quality_name || 'a fault') +
          (held.qc_factor === null ? '' : ' (' + pct(held.qc_factor) + ' credited)') +
          '. The deduction is provisional until the day closes, so the full ' + kg(c.gross_kg) + ' stands for now.'
      )
    else if (inReview)
      out.push(
        'A quality scan at ' + scanClock(inReview) + ' (' + scanName(inReview) + ') is held for review' +
          (inReview.match_reason ? ': ' + lowFirst(inReview.match_reason.replace(/^Held for review: /, '')) : '') +
          ', so its deduction is not applied and the full ' + kg(c.gross_kg) + ' still stands.'
      )
    else if (unapplied)
      out.push(
        unapplied.outcome === 'late'
          ? 'A quality scan at ' + scanClock(unapplied) + ' arrived after the day closed, so its deduction is not applied and the full ' + kg(c.gross_kg) + ' still stands.'
          : 'A quality scan at ' + scanClock(unapplied) + ' could not be tied to one cycle, so its deduction is not applied and the full ' + kg(c.gross_kg) + ' still stands.'
      )
    // h 4514: a cycle with a counter-claim never says "no counter-claim".
    else if (lost.length) out.push('No quality scan reduced it, so the full ' + kg(c.gross_kg) + ' stands.')
    else out.push('No deduction and no counter-claim, so the full ' + kg(c.gross_kg) + ' stands.')
  }
  if (c.deleted) out.push('It was deleted, so it appears in no list, total or share. It is kept so the deletion stays in the audit trail.')
  else if (c.status === 'excluded') out.push('It is excluded from production, so it appears in the record but in no total.')

  // A hand edit is the last word on a cycle, so it is the last thing said.
  const edits = liveEdits(d.adjustments).slice().reverse() // oldest first
  edits.slice(-2).forEach((a, i) => {
    out.push(
      (i === 0 ? 'Then ' : 'After that ') + a.who + ' changed it ' + fmtStamp(a.at) + ': ' + lowFirst(a.what || 'a manual correction') +
        (a.why ? '. Reason given: ' + lowFirst(a.why).replace(/\.$/, '') : '') + '.'
    )
  })
  if (edits.length) out.push('The cycle reads as it does now because of that hand edit, not because of what the engine first decided. Manual authority is never silently undone.')
  if (edits.length > 2) out.push('There are ' + edits.length + ' hand edits in total; the Adjustments tab has all of them.')
  return out
}

export function CycleDrawer({ user, cycleId, workers, onClose, onWritten, toast, onBatch, initialTab = 'timeline', onOpenCycle }: Props) {
  const [detail, setDetail] = useState<Detail | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [tab, setTab] = useState<Tab>(initialTab)
  const [menu, setMenu] = useState(false)
  const [act, setAct] = useState<Act | null>(null)
  const [actVal, setActVal] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const slow = useSlow(busy)

  const userRef = useRef(user)
  userRef.current = user
  const seq = useRef(0)
  const load = useCallback(async () => {
    const n = ++seq.current
    try {
      const d = await apiJson<Detail>(userRef.current, `/api/product-activity/${cycleId}`)
      if (n !== seq.current) return null
      setDetail(d)
      setError(null)
      return d
    } catch (e: any) {
      if (n === seq.current) setError(e?.message || 'Could not load this cycle')
      return null
    }
  }, [cycleId])

  const firstTab = useRef(initialTab)
  useEffect(() => {
    setDetail(null)
    setTab(firstTab.current)
    setMenu(false)
    setAct(null)
    setNote('')
    load()
  }, [load])

  const c = detail?.cycle
  const tone = detail ? (detail.day_state.toLowerCase() as 'live' | 'closed' | 'locked') : 'live'
  const locked = tone === 'locked'

  // h 425-458: the drawer, the Correct menu over it, and the correction form in
  // its foot. Escape closes the menu or cancels the form first, the way its
  // own Cancel does; the drawer closes after, and asks first if the form holds
  // a value nobody has saved. Nothing closes while the write is out (busy).
  const actEl = useRef<HTMLDivElement>(null)
  const [actStart, setActStart] = useState('')
  const drawerEl = useRef<HTMLDivElement>(null)
  const drawer = useLayer(true, onClose, { busy, dirty: !!act && actVal !== actStart && !busy, el: drawerEl, modal: true })
  useLayer(menu, () => setMenu(false))
  useLayer(!!act, () => setAct(null), { busy, el: actEl, focusFirst: true })
  // "Undo the whole bulk edit" (h 2521): what undoing it would do now, read
  // fresh, shown under the entry before anything is undone.
  const [bulkAsk, setBulkAsk] = useState<BulkEdit | null>(null)
  const bulkEl = useRef<HTMLDivElement>(null)
  useLayer(!!bulkAsk, () => setBulkAsk(null), { busy, el: bulkEl, focusFirst: true })

  const write = async (body: Record<string, unknown>) => {
    if (busy) return
    setBusy(true)
    try {
      const r = await apiJson<{ adjustment_id: string; note?: string }>(userRef.current, `/api/product-activity/${cycleId}/adjustments`, { body })
      setAct(null)
      setMenu(false)
      setNote(r.note || '')
      const d = await load()
      const made = d?.adjustments.find((a) => a.id === r.adjustment_id)
      toast(made ? made.what : 'Change saved')
      onWritten()
    } catch (e: any) {
      toast(e?.message || 'The change was not saved')
    } finally {
      setBusy(false)
    }
  }

  // Recognise as a separate pick, and the made cycle's Delete: whole actions
  // with their own routes, since each writes to two cycles.
  const resolveBy = async (path: 'separate' | 'delete', body: Record<string, unknown>) => {
    if (busy) return
    setBusy(true)
    try {
      const r = await apiJson<{ message: string; note?: string }>(userRef.current, `/api/product-activity/${cycleId}/${path}`, { body })
      setAct(null)
      setMenu(false)
      setNote(r.note || '')
      await load()
      toast(r.message)
      onWritten()
    } catch (e: any) {
      toast(e?.message || 'The change was not saved')
    } finally {
      setBusy(false)
    }
  }

  const undo = async (a: Adjustment) => {
    if (busy) return
    setBusy(true)
    try {
      await apiJson(userRef.current, `/api/product-activity/adjustments/${a.id}/revert`, { body: {} })
      await load()
      toast('Reverted: ' + a.what)
      onWritten()
    } catch (e: any) {
      toast(e?.message || 'The undo was not saved')
    } finally {
      setBusy(false)
    }
  }

  const askBulk = async (bulkId: string) => {
    if (busy) return
    setBusy(true)
    try {
      const r = await apiJson<{ bulk_edit: BulkEdit }>(userRef.current, `/api/product-activity/bulk/${bulkId}`)
      setBulkAsk(r.bulk_edit)
    } catch (e: any) {
      toast(e?.message || 'Could not load the bulk edit')
    } finally {
      setBusy(false)
    }
  }

  const undoBulk = async () => {
    if (busy || !bulkAsk) return
    setBusy(true)
    try {
      const r = await apiJson<{ message: string }>(userRef.current, `/api/product-activity/bulk/${bulkAsk.id}/revert`, { body: {} })
      setBulkAsk(null)
      await load()
      toast(r.message)
      onWritten()
    } catch (e: any) {
      toast(e?.message || 'The undo was not saved')
    } finally {
      setBusy(false)
    }
  }

  if (!detail || !c) {
    return (
      <div className={styles.backdrop} onClick={onClose}>
        <div ref={drawerEl} className={`${styles.drawer} ${pv.drawerPanel}`} role="dialog" aria-label="Product cycle" onClick={(e) => e.stopPropagation()}>
          <div className={styles.drawerEmpty}>{error || 'Loading...'}</div>
        </div>
      </div>
    )
  }

  const unapplied = unappliedScans(detail)
  // h 4529-4534: Resolve only where something is genuinely unresolved: an
  // unassigned cycle, a contested claim, an unapplied QC scan, a missing
  // receipt (or weight). A live hand edit to the worker settles a contest,
  // and one to the weight settles a missing receipt; otherwise Correct.
  const handSet = (field: string) => liveEdits(detail.adjustments).some((a) => a.field === field)
  const contested = lostClaims(detail).length > 0 && !handSet('human_id')
  const missing = (awaitingReceipt(c) || c.display_status === 'inferred') && !handSet('gross_weight_kg')
  const resolve = c.flags.includes('unassigned') || unapplied.length > 0 || contested || missing
  const items: { key: string; label: string; hint: string }[] = [
    { key: 'worker', label: c.human_id ? 'Change worker' : 'Set worker', hint: 'Credit this cycle to someone else' },
    { key: 'weight', label: 'Correct gross weight', hint: 'Keeps the original in the audit trail' },
    c.status === 'excluded'
      ? { key: 'include', label: 'Include in production', hint: 'Return this cycle to reporting' }
      : { key: 'exclude', label: 'Exclude from production', hint: 'Keeps the record, drops it from totals' },
  ]
  // h 2714-2725: only when a not-selected claim exists (V4 hasContest).
  const lost = lostClaims(detail)
  if (lost.length) items.push({ key: 'separate', label: 'Recognise as a separate pick', hint: 'Creates an audited second cycle from the not-selected claim' })
  if (unapplied.length) items.push({ key: 'attachqc', label: 'Attach the unmatched QC', hint: 'Applies the deduction to this cycle' })
  // h 2795: the made cycle's delete is the undo of the recognition.
  if (c.recognised_from_cycle_id) items.push({ key: 'delete', label: 'Delete this cycle', hint: 'Undoes the separate pick. The claim goes back as not selected' })

  const run = (key: string) => {
    if (locked) {
      setMenu(false)
      toast(dayLabel(c.work_date) + ' is locked. An administrator must unlock the day first')
      return
    }
    if (key === 'exclude') return write({ field: 'status', value: 'excluded' })
    if (key === 'include') return write({ field: 'status', value: 'included' })
    if (key === 'attachqc') return write({ field: 'qc_factor', quality_scan_id: unapplied[0].id })
    // One not-selected claim is recognised at once, as V4 does; with more,
    // the person says which.
    if (key === 'separate' && lost.length === 1) return resolveBy('separate', { claim_id: lost[0].id })
    setMenu(false)
    setAct(key as Act)
    const start = key === 'weight' ? (c.gross_kg === null ? '' : String(c.gross_kg)) : key === 'worker' ? c.human_id || '' : ''
    setActVal(start)
    setActStart(start)
  }

  const save = () => {
    if (act === 'separate') {
      if (!actVal) return toast('Choose the not-selected claim to recognise')
      return resolveBy('separate', { claim_id: actVal })
    }
    if (act === 'delete') return resolveBy('delete', {})
    if (act === 'weight') {
      const n = parseFloat(actVal)
      if (!Number.isFinite(n) || n <= 0) return toast('Enter a weight in kilograms')
      return write({ field: 'gross_weight_kg', value: n })
    }
    if (act === 'worker') {
      if (!actVal) return toast('Choose a worker')
      return write({ field: 'human_id', value: actVal, resolving: !c.human_id })
    }
  }

  const statusChip = c.deleted
    ? { label: 'Deleted', cls: styles.stExcluded }
    : c.display_status === 'accepted'
      ? { label: 'Accepted', cls: styles.stAccepted }
      : c.display_status === 'inferred'
        ? { label: 'Incomplete', cls: styles.stInferred }
        : { label: 'Excluded', cls: styles.stExcluded }

  const tl = timeline(detail)
  const bt = batchTimeline(detail)
  const edits = liveEdits(detail.adjustments)
  const points = evidencePoints(tl)

  return (
    <div className={styles.backdrop} onClick={drawer.requestClose}>
      <div ref={drawerEl} className={`${styles.drawer} ${pv.drawerPanel}`} role="dialog" aria-label={'Product cycle ' + itemName(c)} onClick={(e) => e.stopPropagation()}>
        <div className={styles.drawerHead}>
          <div className={styles.drawerTitleRow}>
            <div className={styles.drawerTitleCol}>
              <div className={styles.drawerTitle}>{itemName(c)}</div>
              <div className={styles.drawerSub}>
                Product cycle · {dayLabel(c.work_date)}, {c.eff_time || '—'}
              </div>
              {c.tagless && (
                <div className={styles.drawerSub} data-testid="tagless-record">
                  Tagless harvest: no bucket tag. Phone record <span className={styles.itemTail}>{c.item_code}</span>
                </div>
              )}
            </div>
            <div className={styles.drawerChips}>
              <span className={statusChip.cls} data-testid="cycle-status">
                {statusChip.label}
              </span>
              <span className={styles.dayChipSmall}>{STATE_TEXT[tone].name} day</span>
              <button type="button" className={`${o.close} ${pv.drawerClose}`} onClick={drawer.requestClose} title="Close" aria-label="Close" disabled={busy}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" aria-hidden="true">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          <div className={styles.tiles}>
            <div className={styles.tile}>
              <div className={styles.tileLabel}>Worker</div>
              <div className={styles.tileValue}>{c.worker_name || 'Unassigned'}</div>
            </div>
            <div className={styles.tile}>
              <div className={styles.tileLabel}>Weight</div>
              <div className={styles.tileValue}>{c.gross_kg === null ? 'Not weighed' : kg(c.gross_kg)}</div>
            </div>
            <div className={styles.tile}>
              <div className={styles.tileLabel}>Credited</div>
              <div className={styles.tileCredited}>{c.qc_factor !== null ? kg(c.credited_kg) : kg(c.gross_kg)}</div>
            </div>
          </div>
          {c.gross_kg === null && c.estimated_kg ? (
            <div className={styles.estLine} data-testid="estimate-line">
              The site estimates an item at {Number(c.estimated_kg.toFixed(3))} kg. That is a placeholder for counts before weighing: it is never paid, credited or invoiced, and goes when a weight is
              recorded.
            </div>
          ) : null}
          {c.weight_estimated ? (
            <div className={styles.estLine} data-testid="weight-estimated-line">
              Estimated: not weighed, so the site’s item weight is the weight. It counts, credits and pays like a weighed one, and a recorded weight replaces it.
            </div>
          ) : null}
          {c.qc_factor !== null && (
            <div className={styles.factorLine}>
              QC factor {pct(c.qc_factor)} · {c.quality_name || 'Quality'}: gross weight is never overwritten
            </div>
          )}
          {c.qc_factor === null && c.qc_provisional && (
            <div className={styles.factorLine} data-testid="qc-provisional-line">
              QC {c.qc_provisional.qc_factor === null ? '' : pct(c.qc_provisional.qc_factor) + ' '}· {c.qc_provisional.quality_name || 'Quality'}: provisional, applied when the day closes. Credited
              stays at gross until then
            </div>
          )}
          <div className={styles.metaGrid}>
            <div>
              Location <span className={styles.metaVal}> · {c.location || '—'}</span>
            </div>
            <div>
              Picking job <span className={styles.metaVal}> · {c.picking_job_name || '—'}</span>
            </div>
            <div>
              Product <span className={styles.metaVal}> · {c.product || '—'}</span>
            </div>
            <div>
              Batch{' '}
              {c.batch_code && onBatch ? (
                <span className={styles.metaVal}>
                  {' · '}
                  <button type="button" className={styles.batch} title={'Open batch ' + c.batch_code + ' in Batches'} onClick={() => onBatch(c)}>
                    {c.batch_code}
                  </button>
                </span>
              ) : (
                <span className={styles.metaVal}> · {c.batch_code || '—'}</span>
              )}
            </div>
            <div>
              {selfScanned(detail) ? 'Scanned' : 'Receipted'} <span className={styles.metaVal}> · {clock(c.server_received_at)}</span>
            </div>
            <div>
              Source <span className={styles.metaVal}> · {detail.raw_claim?.producer_id || '—'}</span>
            </div>
          </div>
          {c.deleted ? (
            <div className={styles.lockedLine} data-testid="deleted-line">
              This cycle was deleted: it appears in no list, total or share. It is kept so the deletion stays in the audit trail, and can no longer be changed.
            </div>
          ) : c.recognised_from_cycle_id ? (
            // V4's note on a manually made cycle.
            <div className={styles.infoLine} data-testid="separate-line">
              Created by hand from a not-selected claim on {itemName(c)}. Manual authority: automation will not undo this.
            </div>
          ) : null}
          {note && <div className={styles.infoLine}>{note}</div>}
          {locked && <div className={styles.lockedLine}>This day is locked for payroll. Actions are disabled until an administrator unlocks it.</div>}
          {tone === 'live' && !c.deleted && <div className={styles.provLine}>Today is still live. Anything you change here is provisional until the day closes.</div>}
        </div>

        <div className={styles.drawerTabs} role="tablist">
          {(
            [
              ['timeline', 'Timeline'],
              ['evidence', 'Evidence'],
              ['rules', 'Why this cycle'],
              ['audit', 'Adjustments'],
            ] as [Tab, string][]
          ).map(([k, label]) => (
            <button key={k} type="button" role="tab" aria-selected={tab === k} className={tab === k ? pv.tabOn : pv.tab} onClick={() => setTab(k)}>
              {label}
            </button>
          ))}
        </div>

        <div className={styles.drawerBody}>
          {tab === 'timeline' && (
            <div className={styles.tlList}>
              {edits.map((a) => (
                <div key={a.id} className={styles.tlAdj} data-testid="tl-adjustment">
                  <span className={styles.tlDotAdj} />
                  <span className={styles.tlTimeAdj}>{fmtStamp(a.at)}</span>
                  <div className={styles.tlMain}>
                    <div className={styles.tlHead}>
                      <span className={styles.tlKindAdj}>ADJUSTMENT</span>
                      <span className={styles.tlWho}>{a.who}</span>
                      <span className={styles.tlStateAdj}>Manual</span>
                    </div>
                    <div className={styles.tlDetailStrong}>{a.what}</div>
                    <div className={styles.tlRule}>{a.why}</div>
                  </div>
                  {a.can_undo ? (
                    <button type="button" className={styles.tlUndo} onClick={() => undo(a)} disabled={busy}>
                      Undo
                    </button>
                  ) : a.set_by_bin ? (
                    <span className={styles.setByBin}>Set by bin {a.set_by_bin}</span>
                  ) : null}
                </div>
              ))}
              {edits.length > 0 && (
                <div className={styles.tlDivider}>
                  <span />
                  <em>What the app recorded</em>
                  <span />
                </div>
              )}
              {bt.map((b) => {
                const at = stampParts(b.stamp)
                return (
                  <button
                    key={b.key}
                    type="button"
                    className={b.on ? styles.tlBatch : styles.tlBatchOff}
                    data-testid="tl-batch"
                    title={'Open ' + detail.batch!.batch_code + ' in Batches'}
                    disabled={!onBatch}
                    onClick={() => onBatch?.(c)}
                  >
                    <span className={b.on ? styles.tlDotBatch : styles.tlDotOff} />
                    <span className={styles.tlTimeBatch}>
                      {b.stamp ? (
                        <>
                          <small>{at.day}</small>
                          {at.clock}
                        </>
                      ) : (
                        '—'
                      )}
                    </span>
                    <span className={styles.tlMain}>
                      <span className={styles.tlHead}>
                        <span className={b.on ? styles.tlKind : styles.tlKindOff}>{b.kind}</span>
                        <span className={styles.tlWho}>{b.who}</span>
                        <span className={styles.tlStateBatch}>{b.state}</span>
                      </span>
                      <span className={styles.tlDetail}>{b.detail}</span>
                    </span>
                  </button>
                )
              })}
              {tl.map((e) => {
                const counts = e.state === 'selected' || e.state === 'attached'
                const t = TL[e.state]
                return (
                  <div key={e.key} className={counts ? styles.tlOn : styles.tlOff} data-testid="tl-event">
                    <span className={counts ? styles.tlDot : styles.tlDotOff} style={counts ? { background: t.dot } : undefined} />
                    <span className={counts ? styles.tlTime : styles.tlTimeOff}>{e.t}</span>
                    <div className={styles.tlMain}>
                      <div className={styles.tlHead}>
                        <span className={counts ? styles.tlKind : styles.tlKindOff}>{e.kind}</span>
                        <span className={styles.tlWho}>{e.who}</span>
                        <span className={styles.tlState} style={counts ? { background: t.bg, color: t.fg } : undefined}>
                          {t.label}
                        </span>
                      </div>
                      <div className={styles.tlDetail}>{e.detail}</div>
                      {e.photos && <PhotoRow user={user} photos={e.photos} label={`${e.kind.toLowerCase()} ${e.t}`} />}
                      <div className={styles.tlRule}>{e.rule}</div>
                    </div>
                  </div>
                )
              })}
              {!tl.length && !edits.length && !bt.length && <div className={styles.drawerEmpty}>No events recorded for this cycle.</div>}
            </div>
          )}

          {tab === 'evidence' && (
            <div className={styles.evid}>
              <div className={styles.evidTitle}>Where the events happened</div>
              {points.length > 0 && (
                <>
                  <div className={styles.evMap}>
                    <Suspense fallback={null}>
                      <EvidenceMap points={points} />
                    </Suspense>
                  </div>
                  <div className={styles.evCaption}>The dashed line joins the winning claim to its receipt. Hollow points are events the engine did not select.</div>
                </>
              )}
              <div className={styles.evList}>
                {points.map((p) => (
                  <div key={p.key} className={styles.evPoint} data-testid="ev-point">
                    <span className={p.off ? styles.evDotOff : styles.evDot} style={p.off ? { color: p.tone } : { background: p.tone }} />
                    <span className={styles.evTime}>{p.t}</span>
                    <span className={styles.evKind}>{p.kind}</span>
                    <span className={styles.evWho}>{p.who}</span>
                    <span className={styles.evState}>{p.state}</span>
                    <span className={styles.evCoord}>
                      {p.lat.toFixed(5)}, {p.lng.toFixed(5)}
                    </span>
                  </div>
                ))}
                {!points.length && <div className={styles.evNone}>No GPS on any event in this cycle.</div>}
                {!points.length && c.gps && c.gps_from === 'scanner' && (
                  <div className={styles.evNone} data-testid="ev-scanner-gps">
                    Nearest point from the receipter’s scans: {c.gps.lat.toFixed(5)}, {c.gps.lng.toFixed(5)}
                  </div>
                )}
              </div>
              <div className={styles.evidTitle}>Photos</div>
              {tl.some((e) => e.photos?.length) ? (
                tl
                  .filter((e) => e.photos?.length)
                  .map((e) => (
                    <div key={e.key} className={styles.evPhotos}>
                      <div className={styles.evPoint}>
                        <span className={styles.evTime}>{e.t}</span>
                        <span className={styles.evKind}>{e.kind}</span>
                        <span className={styles.evWho}>{e.who}</span>
                      </div>
                      <PhotoRow user={user} photos={e.photos!} label={`${e.kind.toLowerCase()} ${e.t}`} />
                    </div>
                  ))
              ) : (
                <div className={styles.evNone}>No photo has arrived for this cycle. A phone sends its photos after its scans, so one can arrive later.</div>
              )}
              <div className={styles.evSource}>Source · {detail.raw_claim?.producer_id || '—'}</div>
            </div>
          )}

          {tab === 'rules' && (
            <>
              <div className={styles.storyBox}>
                <div className={styles.storyTitle}>What happened</div>
                {story(detail).map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
              {/* h 2777-2782: the server's lines (V4 paRuleLinesRaw), each keyed to the rule that fired; nothing here re-derives them. */}
              <RuleLineList lines={detail.rule_lines || []} />
            </>
          )}

          {tab === 'audit' && (
            <div>
              {detail.adjustments.map((a) => {
                const noteText = a.undone
                  ? 'Reverted'
                  : a.kind === 'change' && a.no_undo
                    ? a.no_undo
                  : a.kind === 'change' && a.set_by_bin
                    ? '' // its reason line already says to change it on the batch
                  : a.kind === 'change' && a.by_merge
                    ? 'Part of a merge: undo it from the merge history in Personnel'
                    : a.kind === 'change' && a.blocked_by_later
                    ? 'Undo the later change to ' + a.field_label.toLowerCase() + ' first'
                    : a.kind === 'change' && locked
                      ? 'Locked day: unlock before undoing'
                      : ''
                return (
                  <div key={a.id} className={styles.adjRow} data-testid="adj-row">
                    <div className={styles.tlMain}>
                      <div className={a.undone ? styles.adjWhatUndone : styles.adjWhat}>{a.what}</div>
                      <div className={styles.adjWhy}>{a.why}</div>
                      <div className={styles.adjWho}>
                        {a.who} · {fmtStamp(a.at)}
                      </div>
                      {noteText && <div className={a.undone ? styles.adjNoteMuted : styles.adjNote}>{noteText}</div>}
                      {a.other_cycle && onOpenCycle && (
                        <button type="button" className={styles.batch} onClick={() => onOpenCycle(a.other_cycle!.id)} disabled={busy}>
                          Open {a.other_cycle.label}
                          {a.other_cycle.deleted ? ' (deleted)' : ''}
                        </button>
                      )}
                      {a.kind === 'change' && a.bulk && (
                        <div className={styles.adjBulk} data-testid="adj-bulk">
                          <span>
                            Part of a bulk edit of {a.bulk.cycles} cycle{a.bulk.cycles === 1 ? '' : 's'}
                            {a.bulk.undone ? ', since undone' : ''}
                          </span>
                          {!a.bulk.undone && bulkAsk?.id !== a.bulk.id && (
                            <button type="button" className={styles.batch} onClick={() => askBulk(a.bulk!.id)} disabled={busy}>
                              Undo the whole bulk edit
                            </button>
                          )}
                        </div>
                      )}
                      {a.kind === 'change' && bulkAsk && bulkAsk.id === a.bulk?.id && (
                        <BulkUndoAsk ask={bulkAsk} busy={busy} elRef={bulkEl} onUndo={undoBulk} onCancel={() => setBulkAsk(null)} />
                      )}
                    </div>
                    {a.can_undo ? (
                      <button type="button" className={styles.ghostSmall} onClick={() => undo(a)} disabled={busy}>
                        Undo
                      </button>
                    ) : a.kind === 'change' && a.set_by_bin ? (
                      // h 3609-3616: where Undo would sit; the only way back
                      // is to re-weigh the bin, which re-shares the set.
                      <span className={styles.setByBin} data-testid="set-by-bin">
                        Set by bin {a.set_by_bin}
                      </span>
                    ) : null}
                  </div>
                )
              })}
              {!detail.adjustments.length && <div className={styles.drawerEmpty}>No manual adjustments. Everything on this cycle came from the engine.</div>}
            </div>
          )}
        </div>

        <div className={pv.foot}>
          {act ? (
            <div ref={actEl} className={styles.actForm}>
              <div className={styles.actTitle}>{ACT_TITLE[act]}</div>
              {act === 'delete' ? (
                <div className={styles.footNote}>
                  The deletion is audited on this cycle and cannot be undone. {c.worker_name ? c.worker_name + '’s claim' : 'The claim'} goes back to the cycle it came from as not selected, where it can be recognised again.
                </div>
              ) : act === 'separate' ? (
                <div className={styles.workerList} role="listbox" aria-label="Not-selected claims">
                  {lost.map((x) => (
                    <button key={x.id} type="button" role="option" aria-selected={actVal === x.id} className={actVal === x.id ? styles.workerOn : styles.workerOpt} onClick={() => setActVal(x.id)}>
                      <span>
                        {x.worker_name || 'Unknown worker'} · {whenOf(c, x.captured_at)}
                      </span>
                      <span className={styles.tick}>{actVal === x.id ? '✓' : ''}</span>
                    </button>
                  ))}
                </div>
              ) : act === 'weight' ? (
                <input className={styles.pillInput} value={actVal} onChange={(e) => setActVal(e.target.value)} placeholder="Kilograms" aria-label="Kilograms" inputMode="decimal" />
              ) : (
                <div className={styles.workerList} role="listbox" aria-label="Workers">
                  {workers.map((w) => (
                    <button key={w.id} type="button" role="option" aria-selected={actVal === w.id} className={actVal === w.id ? styles.workerOn : styles.workerOpt} onClick={() => setActVal(w.id)}>
                      <span>{w.name}</span>
                      <span className={styles.tick}>{actVal === w.id ? '✓' : ''}</span>
                    </button>
                  ))}
                  {!workers.length && <div className={styles.evNone}>No workers in this workspace.</div>}
                </div>
              )}
              <div className={styles.actBtns}>
                <PendingButton busy={busy} pending="Saving…" className={styles.primaryBtn} onClick={save}>
                  {act === 'delete' ? 'Delete and audit' : act === 'separate' ? 'Recognise and audit' : 'Save and audit'}
                </PendingButton>
                <button type="button" className={styles.ghostBtn} onClick={() => setAct(null)} disabled={busy}>
                  Cancel
                </button>
              </div>
            </div>
          ) : c.deleted ? (
            <span className={styles.footNote}>A deleted cycle can no longer be changed. To credit the claim again, recognise it from the cycle it came from.</span>
          ) : (
            <>
              {menu && (
                <div className={pv.actMenu} role="menu">
                  {items.map((a) => (
                    <button key={a.key} type="button" role="menuitem" className={styles.actItem} onClick={() => run(a.key)} disabled={busy}>
                      <span className={styles.actLabel}>{a.label}</span>
                      <span className={styles.actHint}>{a.hint}</span>
                    </button>
                  ))}
                </div>
              )}
              <button type="button" className={pv.menuBtn} onClick={() => setMenu(!menu)} aria-expanded={menu} disabled={busy}>
                {resolve ? 'Resolve' : 'Correct'}
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" aria-hidden="true">
                  <path d="m6 15 6-6 6 6" />
                </svg>
              </button>
              <span className={pv.footNote}>Manual resolutions are authoritative and audited. Automation will not silently undo them.</span>
            </>
          )}
        </div>
      </div>
      {drawer.confirm}
    </div>
  )
}

// What undoing a bulk edit would do, before it is done: how many cycles go
// back, which are kept (undone on their own or changed since) and, while any
// is on a locked day, that nothing can be undone until it is unlocked.
function BulkUndoAsk({ ask, busy, elRef, onUndo, onCancel }: { ask: BulkEdit; busy: boolean; elRef: React.RefObject<HTMLDivElement>; onUndo: () => void; onCancel: () => void }) {
  const u = ask.undo
  const n = u?.reverting || 0
  return (
    <div ref={elRef} className={styles.bulkAsk} role="group" aria-label="Undo the whole bulk edit">
      <div className={styles.bulkAskTitle}>Undo “{ask.what}”?</div>
      {!u ? (
        <div>This bulk edit has already been undone.</div>
      ) : (
        <>
          <div>
            {n
              ? `${n} of the ${ask.changed_count} cycle${ask.changed_count === 1 ? '' : 's'} it changed go${n === 1 ? 'es' : ''} back to what ${n === 1 ? 'it was' : 'they were'} before it, in one action.`
              : 'Nothing is left to undo: every change in it has been undone or changed since.'}
          </div>
          {u.kept_text.length > 0 && <div>Kept as they are: {u.kept_text.join(', ')}.</div>}
          {u.locked_dates.length > 0 && (
            <div className={styles.adjNote}>
              On a locked day ({u.locked_dates.map(dayLabel).join(', ')}). An administrator must unlock it first; nothing is undone until then.
            </div>
          )}
        </>
      )}
      <div className={styles.bulkAskBtns}>
        {u?.can_undo && (
          <button type="button" className={styles.ghostSmall} onClick={onUndo} disabled={busy}>
            Undo {n} cycle{n === 1 ? '' : 's'}
          </button>
        )}
        <button type="button" className={styles.ghostSmall} onClick={onCancel} disabled={busy}>
          {u?.can_undo ? 'Cancel' : 'Close'}
        </button>
      </div>
    </div>
  )
}
